import { useTranslation } from "@sagot/i18n";
import { Button, EmptyState, ErrorState, Icon, Skeleton, StatusBadge, type StatusKind } from "@sagot/ui";
import { useState } from "react";
import { useFormatters } from "../shared/format";
import { RunSheet } from "./run-sheet";
import type { RunOut } from "./types";
import { useWorkflowRuns } from "./use-workflows";

const R = "engage.workflows.runs";
const RUN_STATUS: Record<string, StatusKind> = { running: "info", waiting: "warning", delegated: "info", completed: "success", failed: "danger", cancelled: "neutral", skipped: "warning" };

/** Recent runs of one workflow, newest first; a run opens its step timeline. */
export function RunsPanel({ workflowId }: { workflowId: string }) {
  const { t } = useTranslation();
  const format = useFormatters();
  const runs = useWorkflowRuns(workflowId, true);
  const [open, setOpen] = useState<string | null>(null);
  if (runs.isError) return <ErrorState title={t("engage.saveFailed")} onRetry={() => void runs.refetch()} retryLabel={t("common.retry")} />;
  if (runs.isPending) return <Skeleton className="h-48 rounded-card" />;
  const items: RunOut[] = runs.data.pages.flatMap((page) => page.items);
  if (items.length === 0) return <EmptyState icon="activity" headingLevel={3} title={t(`${R}.title`)} description={t(`${R}.empty`)} />;
  return (
    <section className="card relative overflow-x-auto" aria-label={t(`${R}.title`)}>
      <table className="table">
        <thead>
          <tr>
            <th>{t(`${R}.started`)}</th>
            <th>{t(`${R}.status`)}</th>
            <th>{t(`${R}.path`)}</th>
            <th>{t(`${R}.version`)}</th>
            <th><span className="sr-only">{t(`${R}.view`)}</span></th>
          </tr>
        </thead>
        <tbody>
          {items.map((run) => (
            <tr key={run.id}>
              <td className="whitespace-nowrap tabular-nums text-fg-muted">{format.dateTime(run.started_at)}</td>
              <td>
                <span className="flex flex-wrap items-center gap-1.5">
                  <StatusBadge status={RUN_STATUS[run.status] ?? "neutral"} label={t(`${R}.statuses.${run.status}`, { defaultValue: run.status })} />
                  {run.goal_met_at ? <Icon name="target" className="size-4 text-success" label={t(`${R}.goalReached`)} /> : null}
                </span>
              </td>
              <td className="font-mono text-xs">{run.path}</td>
              <td className="font-mono text-xs">{run.version ? `v${String(run.version)}` : "—"}</td>
              <td className="text-right"><Button variant="ghost" size="sm" onClick={() => { setOpen(run.id); }}>{t(`${R}.view`)}</Button></td>
            </tr>
          ))}
        </tbody>
      </table>
      {runs.hasNextPage ? (
        <div className="flex justify-center border-t border-line p-3">
          <Button variant="secondary" size="sm" loading={runs.isFetchingNextPage} onClick={() => void runs.fetchNextPage()}>{t(`${R}.loadMore`)}</Button>
        </div>
      ) : null}
      {open ? <RunSheet workflowId={workflowId} runId={open} onClose={() => { setOpen(null); }} /> : null}
    </section>
  );
}
